import { ErrorHandler, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, RouterModule, UrlSerializer } from '@angular/router';
import { PaginationControlsDirective } from 'ngx-pagination';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './shared/header/header.component';
import { MyMissingTranslationHandler } from './shared/translate/missing-translation-handler';
import { FooterComponent } from './shared/footer/footer.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import 'chartjs-adapter-moment';
import { ToastrModule } from 'ngx-toastr';
import { QrPaymentComponent } from './shared/qr-payment/qr-payment.component';
import { ToastNotificationComponent } from './shared/toast-notification/toast-notification.component';
import { ModalComponent } from './services/modal/modal/modal.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { WebpackTranslateLoader } from './shared/translate/translate-loader';
import {  ConfigService, provideConfig } from './services/json-config.service';
import { DisclaimerComponent } from './shared/disclaimer/disclaimer.component';
import { CustomUrlSerializer } from './shared/custom-url-serializer';
import { AuthInterceptor } from './shared/token-refresh.interceptor';
import { CustomRoutingService } from './services/custom-routing.service';
import { SummaryModule } from './modules/summary/summary.module';
import { ButtonModule } from './shared/button/button.module';
import { TooltipButtonModule } from './shared/tooltip-button/tooltip-button.module';
import { PreloadSpinnerModule } from './shared/preload-spinner/preload-spinner.module';
import { PricePipeModule } from './pipes/price.module';
import { PortfolioModule } from './modules/portfolio/portfolio.module';
import { VideosModule } from './modules/videos/videos.module';
import { StrategyModule } from './modules/strategy/strategy.module';
import { DocumentsModule } from './modules/documents/documents.module';
import { UserModule } from './modules/user/user.module';
import { ThematicPortfoliosModule } from './modules/thematic-portfolios/thematic-portfolios.module';
import { ModelPortfoliosModule } from './modules/model-portfolios/model-portfolios.module';
import { AboutModule } from './modules/about/about.module';
import { OwnStrategiesModule } from './modules/own-strategies/own-strategies.module';
import { HowWeInvestModule } from './modules/how-we-invest/how-we-invest.module';
import { DictionaryModule } from './modules/dictionary/dictionary.module';
import { MoneyModule } from './modules/money/money.module';
import { BusinessCalendarModule } from './modules/business-calendar/business-calendar.module';
import { InvestmentsModule } from './modules/investments/investments.module';
import { BlogModule } from './modules/blog/blog.module';
import { OauthRoutingModule } from './modules/oauth/oauth-routing.module';
import { DipRedirectConfirmComponent } from './pages/dip-redirect-confirm/dip-redirect-confirm.component';
import { AboutDipModule } from './modules/about-dip/about-dip.module';
import { LiquidationDisclaimerComponent } from './shared/liquidation-disclaimer/liquidation-disclaimer.component';
import { AdditionalSignRedirectComponent } from './pages/additional-sign-redirect/additional-sign-redirect.component';
import { LocaleCountryNamePipe } from './pipes/locale-country-name.pipe';
import { InitSignInModalComponent } from './services/modal/modal-contents/init-sign-in-modal/init-sign-in-modal.component';
import * as Sentry from '@sentry/angular';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { DraftAcceptRefuseConfirmModalComponent } from './services/modal/modal-contents/draft-accept-refuse-confirm-modal/draft-accept-refuse-confirm-modal.component';
import { ClientAdditionsRedirectComponent } from './pages/client-additions-redirect/client-additions-redirect.component';
import { registerLocaleData } from '@angular/common';
import localeCs from '@angular/common/locales/cs';
import { UnauthFooterComponent } from './shared/unauth-footer/unauth-footer.component';
import { FeesConfigDropdownComponent } from './shared/fees-config-dropdown/fees-config-dropdown.component';
import { FeesEntryRegularConfigDropdownComponent } from './shared/fees-entry-regular-config-dropdown/fees-entry-regular-config-dropdown.component';
import { ClientAcceptEfektikaDraftStrategyModalComponent } from './services/modal/modal-contents/client-accept-efektika-draft-strategy-modal/client-accept-efektika-draft-strategy-modal.component';
import { ProfileFeesConfirmModalComponent } from './services/modal/modal-contents/profile-fees-confirm-modal/profile-fees-confirm-modal.component';
import { EmailSigningComponent } from './pages/email-signing/email-signing.component';
import { NewRegularWithdrawalModalComponent } from './services/modal/modal-contents/new-regular-withdrawal-modal/new-regular-withdrawal-modal.component';
import { WithdrawalDraftCommentModalComponent } from './services/modal/modal-contents/withdrawal-draft-comment-modal/withdrawal-draft-comment-modal.component';
import { ConsultImageComponent } from './shared/consult-image/consult-image.component';
import { TransactionsExportModalComponent } from './services/modal/modal-contents/transactions-export-modal/transactions-export-modal.component';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';

registerLocaleData(localeCs);

@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        DisclaimerComponent,
        FooterComponent,
        UnauthFooterComponent,
        ModalComponent,
        ToastNotificationComponent,
        QrPaymentComponent,
        NotFoundComponent,
        DipRedirectConfirmComponent,
        LiquidationDisclaimerComponent,
        AdditionalSignRedirectComponent,
        EmailSigningComponent,
        ClientAdditionsRedirectComponent,
        LocaleCountryNamePipe,
        InitSignInModalComponent,
        DraftAcceptRefuseConfirmModalComponent,
        FeesConfigDropdownComponent,
        FeesEntryRegularConfigDropdownComponent,
        ClientAcceptEfektikaDraftStrategyModalComponent,
        ProfileFeesConfirmModalComponent,
        NewRegularWithdrawalModalComponent,
        WithdrawalDraftCommentModalComponent,
        ConsultImageComponent,
        TransactionsExportModalComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
        RouterModule,
        BrowserModule,
        AppRoutingModule,
        ReactiveFormsModule,
        FormsModule,
        SummaryModule,
        PortfolioModule,
        VideosModule,
        StrategyModule,
        DocumentsModule,
        UserModule,
        ThematicPortfoliosModule,
        ModelPortfoliosModule,
        AboutModule,
        OwnStrategiesModule,
        HowWeInvestModule,
        DictionaryModule,
        MoneyModule,
        BusinessCalendarModule,
        AboutDipModule,
        InvestmentsModule,
        BlogModule,
        ButtonModule,
        PreloadSpinnerModule,
        TooltipButtonModule,
        PricePipeModule,
        OauthRoutingModule,
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: WebpackTranslateLoader,
                deps: [HttpClient],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MyMissingTranslationHandler,
            }
        }),
        AngularSvgIconModule.forRoot(),
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            toastComponent: ToastNotificationComponent,
            preventDuplicates: true,
        })],
        providers: [
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AuthInterceptor,
                multi: true
            },
            TranslatePipe,
            PaginationControlsDirective,
            provideConfig(),
            ConfigService,
            // ConfigModule.init(),
            {
                provide: UrlSerializer,
                useClass: CustomUrlSerializer
            },
            CustomRoutingService,
            {
                provide: ErrorHandler,
                useValue: Sentry.createErrorHandler(),
            },
            {
                provide: Sentry.TraceService,
                deps: [Router],
            },
            {
                provide: provideAppInitializer,
                useFactory: () => () => null,
                deps: [Sentry.TraceService],
                multi: true,
            },
            provideHttpClient(withInterceptorsFromDi()),
            provideCharts(withDefaultRegisterables()),
            { provide: LOCALE_ID, useValue: 'cs-CZ'},
        ]
    })
export class AppModule { }
